import { isPlatformServer } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
// Consider moving functionality to shared folder:
// eslint-disable-next-line @nx/enforce-module-boundaries
import { LogoutModal } from '@authentication/feature-login';
import { AppRoutesEnum } from '@inshared/shared/core';
import { NativeRouterService, SalesfunnelService } from '@inshared/shared/util';
import { StorageService } from '@inshared/shared/util-storage';
import {
    AuthenticationActionService,
    AuthenticationDataService,
    NewModal,
    RelationDataService,
    RelationInterface,
} from 'outshared-lib';
import { firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';

const TARGET_URL = 'targetUrl';

@Component({
    selector: 'ins-autorisatie-menu',
    templateUrl: './autorisatie-menu.component.html',
    styleUrls: ['./autorisatie-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutorisatieMenuComponent implements OnDestroy {
    private readonly subscriptions: Subscription = new Subscription();
    private readonly router = inject(Router);
    private readonly newModal = inject(NewModal);
    private readonly authenticationDataService = inject(AuthenticationDataService);
    private readonly authenticationActionService = inject(AuthenticationActionService);
    private readonly salesfunnelService = inject(SalesfunnelService);
    private readonly storage = inject(StorageService);

    public relation$: Observable<RelationInterface> = inject(RelationDataService).getRelation$();
    public authenticated$: Observable<boolean> = this.authenticationDataService.isLoggedIn$();
    public nativeRouterService = inject(NativeRouterService);
    public jaarbeloning: number | null = null;
    public aantal: number | null = null;
    public appRoutesEnum = AppRoutesEnum;
    public baseUrl = this.nativeRouterService.baseUrl;
    public isContactHoverState: boolean = false;

    public readonly isServerPlatform = isPlatformServer(inject(PLATFORM_ID));

    public get currentUrl(): string {
        return this.router.routerState.snapshot.url;
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public gotoLogin(): boolean {
        if (this.salesfunnelService.isSalesfunnel(this.currentUrl)) {
            this.storage.setItem(TARGET_URL, JSON.stringify(this.currentUrl));
        } else {
            this.storage.removeItem(TARGET_URL);
        }
        this.nativeRouterService.navigateByUrl(`${this.baseUrl}/${AppRoutesEnum.Login}`);

        return false;
    }

    public onMobileMenuClick(event: CustomEvent) {
        const link = event.detail?.link as HTMLAnchorElement;
        const href = link?.getAttribute('href');
        const knownRoute = href ? this.isKnownRoute(href) : false;

        const navigationEnd = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));
        firstValueFrom(navigationEnd).then(() => {
            const mobileMenu = event.detail.mobileMenu;
            if (mobileMenu && mobileMenu.checkForCurrentLink) {
                mobileMenu.checkForCurrentLink();
            }
        });

        if (!link || !knownRoute || link.dataset['menuLink']) {
            // no link, SSG url, or a submenu link; let component handle this
            return;
        }

        if (href) {
            event.preventDefault(); // tell stencil we'll handle this
            event.detail.mobileMenu?.close();
            this.router.navigateByUrl(href);
        }
    }

    /**
     * Ask for sign out confirmation when user has a long-lasting token
     */
    public onSignOut(event: Event): void {
        event.preventDefault();

        this.authenticationDataService
            .getState$()
            .pipe(
                first(),
                map((state) => state?.token),
                switchMap((token) => (token ? this.newModal.open(LogoutModal).afterClosed$() : of(true)))
            )
            .subscribe((confirmed) => (confirmed ? this.handleSignOut() : null));
    }

    private isKnownRoute(path: string): boolean {
        // hardcoded check on an angularJS page behind a valid route entry
        if (path.includes('/mijn-verzekeringsmap/schaderegelen')) {
            return false;
        }

        const segmentReg = /\/([^/]+)/;
        const segment = segmentReg.test(path) ? segmentReg.exec(path)![1] : false;
        return segment ? !!this.router.config.find(({ path }) => path === segment) : false;
    }

    private handleSignOut(): void {
        this.authenticationActionService.logout$().subscribe({
            next: () => this.handleAfterSignOut(),
            error: () => this.handleAfterSignOut(),
        });
    }

    private handleAfterSignOut(): void {
        if (!this.salesfunnelService.isSalesfunnel(this.currentUrl)) {
            this.nativeRouterService.navigateByUrl(`${this.baseUrl}/${AppRoutesEnum.Logout}`);
        }
    }

    public isHovering(element: HTMLElement): boolean {
        return element.style.color === '';
    }
}
